import { formatDate, useLocale, useTranslation } from '@multaqa/i18n'
import { useCallback } from 'react'

/**
 * Monthly or quarterly fiscal periods (f287).
 *
 * The API does not store a year's period type: a year's periods are the
 * record of it. A monthly period starts and ends in one calendar month; a
 * fiscal quarter spans three, so the dates alone tell them apart.
 */
export type FiscalPeriodType = 'monthly' | 'quarterly'

type PeriodDates = { starts_on: string; ends_on: string }

/** Whether the period is a fiscal quarter (it spans more than one month). */
export function isQuarterPeriod(period: PeriodDates): boolean {
  return period.starts_on.slice(0, 7) !== period.ends_on.slice(0, 7)
}

/** The period type a year was created with; a year with no periods reads as monthly. */
export function yearPeriodType(year: { periods: PeriodDates[] }): FiscalPeriodType {
  const first = year.periods[0]
  return first && isQuarterPeriod(first) ? 'quarterly' : 'monthly'
}

/**
 * The name a lock or unlock action gives a period: "period 3" for a month,
 * "Q1 · Jan – Mar" for a quarter, the same words the periods table shows.
 */
export function usePeriodName(): (period: PeriodDates & { sequence: number }) => string {
  const { t } = useTranslation('accounting')
  const { locale } = useLocale()
  return useCallback(
    (period) =>
      isQuarterPeriod(period)
        ? `${t('periods.quarter', { n: period.sequence })} · ${t('periods.monthSpan', {
            from: formatDate(new Date(period.starts_on), locale, 'MMM'),
            to: formatDate(new Date(period.ends_on), locale, 'MMM'),
          })}`
        : t('periods.periodNumber', { n: period.sequence }),
    [t, locale],
  )
}
